import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface CreateExportedFolderModel {
  folderName: string;
  description?: string;
  orderIds: number[];
  filterCriteria?: any;
}

export interface CreateBasicExportedFolderModel {
  folderName: string;
  description?: string;
  orderCount: number;
  sparePartCount: number;
  totalSize: number;
  fileName: string;
}

export interface ExportedFolderOutModel {
  folderId: number;
  folderName: string;
  description: string;
  exportDate: Date;
  userId: number;
  userName: string;
  orderCount: number;
  totalSize: number;
  totalSizeFormatted: string;
  filterCriteria?: any;
  folderPath: string;
  isDeleted: boolean;
  createdAt: Date;
  updatedAt: Date;
  exportedOrders: ExportedFolderOrderOutModel[];
}

export interface ExportedFolderOrderOutModel {
  id: number;
  folderId: number;
  orderId: number;
  exportedAt: Date;
  orderSnapshot: any;
}

@Injectable({
  providedIn: 'root'
})
export class ExportedFoldersService {
  private apiUrl = environment.apiEndpoint + '/ExportedFolder';

  constructor(private http: HttpClient) { 
  }

  createExportedFolder(model: CreateExportedFolderModel): Observable<ExportedFolderOutModel> {
    // Los headers con información del usuario se envían automáticamente
    // a través de las cookies o interceptors de autenticación
    return this.http.post<ExportedFolderOutModel>(`${this.apiUrl}/create`, model, { 
      withCredentials: true 
    }).pipe(
      catchError(error => {
        console.error('Error en la petición HTTP:', error);
        return throwError(error);
      })
    );
  }

  createBasicExportedFolder(model: CreateBasicExportedFolderModel): Observable<ExportedFolderOutModel> {
    return this.http.post<ExportedFolderOutModel>(`${this.apiUrl}/create-basic`, model, {
      withCredentials: true
    }).pipe(
      catchError(error => {
        console.error('Error al crear registro básico de exportación:', error);
        return throwError(error);
      })
    );
  }

  // Método para cancelar la exportación (si el backend lo soporta)
  cancelExport(folderId?: number): Observable<void> {
    const url = folderId ? `${this.apiUrl}/cancel/${folderId}` : `${this.apiUrl}/cancel`;
    return this.http.post<void>(url, {}, { 
      withCredentials: true 
    }).pipe(
      catchError(error => {
        console.error('Error al cancelar exportación:', error);
        return throwError(error);
      })
    );
  }

  getExportedFolders(userId: number = 0, includeDeleted: boolean = false, includeContent: boolean = false): Observable<ExportedFolderOutModel[]> {
    let params = new HttpParams();
    if (userId > 0) {
      params = params.set('userId', userId.toString());
    }
    if (includeDeleted) {
      params = params.set('includeDeleted', 'true');
    }
    if (includeContent) {
      params = params.set('includeContent', 'true');
    }

    return this.http.get<ExportedFolderOutModel[]>(this.apiUrl, { 
      params, 
      withCredentials: true 
    });
  }

  getExportedFolder(folderId: number): Observable<ExportedFolderOutModel> {
    return this.http.get<ExportedFolderOutModel>(`${this.apiUrl}/${folderId}`, {
      withCredentials: true
    });
  }

  getFolderOrders(folderId: number, searchTerm: string = '', page: number = 1, pageSize: number = 50): Observable<{orders: ExportedFolderOrderOutModel[], total: number, page: number, pageSize: number, totalPages: number}> {
    let params = new HttpParams();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    params = params.set('page', page.toString());
    params = params.set('pageSize', pageSize.toString());

    return this.http.get<{orders: ExportedFolderOrderOutModel[], total: number, page: number, pageSize: number, totalPages: number}>(`${this.apiUrl}/${folderId}/orders`, { 
      params,
      withCredentials: true 
    });
  }

  deleteExportedFolder(folderId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${folderId}`, {
      withCredentials: true
    });
  }



  // Generar nombre por defecto para carpeta
  generateDefaultFolderName(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    
    return `Exportacion_${year}_${month}_${day}_${hours}${minutes}`;
  }
} 